import { List, Map, fromJS } from "immutable";
import isDeepEqual from "stores/isDeepEqual";
import Widget from "views/logic/widgets/Widget";
import WidgetPosition from "views/logic/widgets/WidgetPosition";
import TitleTypes from "views/stores/TitleTypes";
import FormattingSettings from "./formatting/FormattingSettings";
export default class ViewState {
  constructor(fields, titles, widgets, widgetMapping, widgetPositions, formatting, staticMessageListId) {
    this._value = {
      fields,
      titles,
      widgets: List(widgets),
      widgetMapping,
      widgetPositions: Map(widgetPositions),
      formatting,
      staticMessageListId
    };
  }
  static create() {
    return new Builder().widgets(List()).widgetPositions(Map()).titles(Map()).build();
  }
  get fields() {
    return this._value.fields;
  }
  get formatting() {
    return this._value.formatting;
  }
  get titles() {
    return this._value.titles;
  }
  get widgets() {
    return this._value.widgets;
  }
  get widgetMapping() {
    return this._value.widgetMapping;
  }
  get widgetPositions() {
    return this._value.widgetPositions.toJS();
  }
  get staticMessageListId() {
    return this._value.staticMessageListId;
  }
  duplicate() {
    const widgetIdTranslation = {};
    const newWidgets = this.widgets.map((widget) => {
      const newWidget = widget.toBuilder().newId().build();
      widgetIdTranslation[widget.id] = newWidget.id;
      return newWidget;
    }).toList();
    const newWidgetTitles = Map(
      this.titles.get(TitleTypes.Widget, Map()).mapEntries(([key, value]) => [widgetIdTranslation[key], value])
    );
    const newTitles = this.titles.set(TitleTypes.Widget, newWidgetTitles).updateIn([TitleTypes.Tab, "title"], (value) => value ? `${value} (Copy)` : value);
    const newWidgetPositions = Map(this.widgetPositions).mapEntries(([key, value]) => [widgetIdTranslation[key], value]).toJS();
    return this.toBuilder().widgetMapping(Map()).widgetPositions(newWidgetPositions).widgets(newWidgets).titles(newTitles).build();
  }
  toBuilder() {
    const value = this._value;
    return new Builder(Map(value));
  }
  equals(other) {
    if (other === void 0) {
      return false;
    }
    if (!(other instanceof ViewState)) {
      return false;
    }
    return isDeepEqual(this.fields, other.fields) && isDeepEqual(this.titles, other.titles) && isDeepEqual(this.widgets, other.widgets) && isDeepEqual(this.widgetMapping, other.widgetMapping) && isDeepEqual(this.widgetPositions, other.widgetPositions) && isDeepEqual(this.formatting, other.formatting);
  }
  toJSON() {
    const { fields, formatting, titles, widgets, widgetMapping, widgetPositions } = this._value;
    return {
      selected_fields: fields,
      formatting,
      titles,
      widgets,
      widget_mapping: widgetMapping,
      positions: widgetPositions
    };
  }
  static fromJSON(value) {
    const {
      selected_fields: selectedFields,
      titles,
      widgets,
      widget_mapping: widgetMapping,
      positions,
      formatting
    } = value;
    return ViewState.builder().titles(fromJS(titles)).widgets(List(widgets.map((w) => Widget.fromJSON(w)))).widgetMapping(fromJS(widgetMapping)).fields(selectedFields).widgetPositions(Map(positions).map(WidgetPosition.fromJSON).toMap()).formatting(formatting ? FormattingSettings.fromJSON(formatting) : FormattingSettings.empty()).build();
  }
  static builder() {
    return new Builder();
  }
}
class Builder {
  constructor(value = Map()) {
    this.value = value;
  }
  fields(value) {
    return new Builder(this.value.set("fields", value));
  }
  formatting(value) {
    return new Builder(this.value.set("formatting", value));
  }
  titles(value) {
    return new Builder(this.value.set("titles", fromJS(value)));
  }
  widgets(value) {
    return new Builder(this.value.set("widgets", List(value)));
  }
  widgetMapping(value) {
    return new Builder(this.value.set("widgetMapping", value));
  }
  widgetPositions(value) {
    return new Builder(this.value.set("widgetPositions", Map(value)));
  }
  build() {
    const { fields, formatting, titles, widgets, widgetMapping, widgetPositions } = this.value.toObject();
    return new ViewState(fields, titles, widgets, widgetMapping, widgetPositions, formatting);
  }
}
